import { Action, AnyAction } from 'redux'
import { Epic, ofType } from 'redux-observable'
import { catchError, EMPTY, from, mergeMap, of, switchMap, timer } from 'rxjs'
import { ErrorMessageContents, getHub } from '../helpers/hub'
import { logError } from '../store/ws/actions'
import { InvokeDefinition } from './types'

/*
  Using timer() instead of delay() so a superseded switch trigger never
  reaches the hub — see the note in createStreamEpic.
*/
export const createInvokeEpic = <TParams, TResponse>(
  triggerAction: string,
  /* `any` for TAction: the trigger-string-to-action-type link can't be checked here */
  definition: InvokeDefinition<TParams, TResponse, any>
): Epic<Action> => {
  /* default 'merge': deliver every response; see InvokeDefinition.strategy */
  const flattenOp = definition.strategy === 'switch' ? switchMap : mergeMap
  const mapErrorToActions: (err: unknown, action: Action) => AnyAction[] =
    definition.mapError
      ? (err, action) =>
          [definition.mapError!(err as ErrorMessageContents, action)].flat()
      : () => []

  return (action$, state$) =>
    action$.pipe(
      ofType(triggerAction),
      flattenOp((action: Action) => {
        const params = definition.getParams(state$.value, action)
        const paramsArray = Array.isArray(params) ? params : [params]

        const callHub = () =>
          getHub().invoke<TResponse>(definition.method, ...paramsArray)
        const source$ = definition.delayMs
          ? timer(definition.delayMs).pipe(mergeMap(callHub))
          : callHub()

        return source$.pipe(
          definition.mapResponse
            ? mergeMap((response) => {
                const result = definition.mapResponse!(response, action)
                return Array.isArray(result) ? from(result) : of(result)
              })
            : mergeMap(() => EMPTY),
          catchError((err: unknown) =>
            from([...mapErrorToActions(err, action), logError(err)])
          )
        )
      })
    )
}
